import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ArrowRight } from "lucide-react";
import { useCategories } from "@/lib/queries";
import { useSettings } from "@/context/SettingsContext";
import { telLink } from "@/lib/contact";

const NAV = [
  { to: "/", label: "Ana Sayfa" },
  { to: "/hizmetler", label: "Hizmetler", mega: true },
  { to: "/uygulamalar", label: "Uygulamalar" },
  { to: "/blog", label: "Blog" },
  { to: "/iletisim", label: "İletişim" },
];

/** Brand lockup: big dominant logo + gold divider + İZMİT wordmark */
function BrandLockup({ scrolled }) {
  return (
    <Link to="/" data-testid="header-logo" className="group flex items-center gap-3 sm:gap-4">
      <img
        src="/logo.png"
        alt="Meguiar's İzmit"
        className={`w-auto object-contain drop-shadow-[0_3px_10px_rgba(0,0,0,0.55)] transition-[height] duration-300 ${
          scrolled ? "h-11 sm:h-12" : "h-14 sm:h-16"
        }`}
      />
      <span
        className={`w-px bg-gradient-to-b from-transparent via-gold/70 to-transparent transition-[height] duration-300 ${
          scrolled ? "h-9 sm:h-10" : "h-11 sm:h-12"
        }`}
      />
      <span className="flex flex-col leading-none">
        <span className="font-display text-xl font-bold uppercase tracking-[0.28em] text-white sm:text-2xl">
          İZMİT
        </span>
        <span className="mt-1.5 hidden text-[9px] font-semibold uppercase tracking-[0.26em] text-gold sm:block">
          Premium Araç Koruma Merkezi
        </span>
      </span>
    </Link>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const { data: categories = [] } = useCategories();
  const settings = useSettings();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "glass border-b border-white/10 py-2.5"
          : "border-b border-transparent bg-gradient-to-b from-black/70 via-black/25 to-transparent py-4 backdrop-blur-[6px]"
      }`}
    >
      <div className="container-x flex items-center justify-between gap-4">
        <BrandLockup scrolled={scrolled} />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) =>
            item.mega ? (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={() => setMegaOpen(true)}
                onMouseLeave={() => setMegaOpen(false)}
              >
                <NavLink
                  to={item.to}
                  data-testid="nav-hizmetler"
                  className={({ isActive }) =>
                    `flex items-center gap-1 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${
                      isActive ? "text-gold" : "text-neutral-300 hover:text-white"
                    }`
                  }
                >
                  {item.label} <ChevronDown className={`h-4 w-4 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
                </NavLink>
                {megaOpen && (
                  <div className="absolute left-1/2 top-full w-[640px] -translate-x-1/2 pt-3">
                    <div data-testid="mega-menu" className="grid grid-cols-2 gap-1.5 rounded-2xl border border-white/10 bg-neutral-900/95 p-3 shadow-2xl backdrop-blur-xl">
                      {categories.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/hizmetler/kategori/${c.slug}`}
                          className="group flex flex-col rounded-xl px-4 py-3 transition-colors hover:bg-white/5"
                        >
                          <span className="flex items-center gap-2 text-sm font-semibold text-white group-hover:text-gold">
                            {c.name} <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                          </span>
                          <span className="mt-1 text-xs text-neutral-400 line-clamp-1">{c.short}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${isActive ? "text-gold" : "text-neutral-300 hover:text-white"}`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a href={telLink(settings)} className="flex items-center gap-2 text-sm font-semibold text-white hover:text-gold transition-colors">
            <Phone className="h-4 w-4 text-gold" /> {settings?.phone}
          </a>
          <Link
            to="/randevu"
            data-testid="header-randevu-btn"
            className="rounded-xl bg-gold px-5 py-2.5 text-sm font-bold text-black shadow-[0_6px_20px_-6px_rgba(245,197,66,0.6)] hover:bg-gold-300 transition-colors"
          >
            Randevu Al
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-white lg:hidden"
          aria-label="Menü"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden">
          <div className="container-x mt-3 space-y-1 rounded-2xl border border-white/10 bg-neutral-900/95 p-3 backdrop-blur-xl">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `block rounded-xl px-4 py-3 text-sm font-medium ${isActive ? "bg-white/5 text-gold" : "text-neutral-200"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/randevu" className="mt-2 block rounded-xl bg-gold px-4 py-3 text-center text-sm font-bold text-black">
              Randevu Al
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
